import { useQuery } from '@tanstack/react-query';

import { fbmSubstitutionRulesV1Api } from 'api/fbmSubstitutionRulesV1';
import { ApiError } from 'shared/errors/ApiError';

export const SUBSTITUTION_RULE_EVENTS_QUERY_KEY = 'fbmSubstitutionRuleEvents';

export const substitutionRuleEventsQueryKeys = {
    all: [SUBSTITUTION_RULE_EVENTS_QUERY_KEY],
    account: (fbmAccountName) => [SUBSTITUTION_RULE_EVENTS_QUERY_KEY, fbmAccountName],
    list: (fbmAccountName, facilityId) => [SUBSTITUTION_RULE_EVENTS_QUERY_KEY, fbmAccountName, facilityId],
};

/**
 * Fetches substitution rule events for an FBM account.
 *
 * @param {Object} options
 * @param {string} [options.fbmAccountName] - FBM account name (from user context)
 * @param {number|string} [options.facilityId] - Facility ID (from user context)
 * @param {boolean} [options.enabled=true] - Whether the query should run
 */
export function useSubstitutionRuleEventsQuery({ enabled = true, facilityId, fbmAccountName } = {}) {
    return useQuery({
        enabled: enabled && !!fbmAccountName,
        queryFn: async () => {
            const response = await fbmSubstitutionRulesV1Api.getEvents(fbmAccountName, facilityId);
            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to fetch substitution rule events',
                    response?.statusCode ?? 400,
                    response?.data,
                );
            }
            return response.data?.rows ?? [];
        },
        queryKey: substitutionRuleEventsQueryKeys.list(fbmAccountName, facilityId),
        retry: (failureCount, err) => {
            if (err instanceof ApiError && (err.isServerError || err.isAuthError || err.isClientError)) {
                return false;
            }
            return failureCount < 2;
        },
        staleTime: 60_000,
    });
}
